/* eslint-disable max-lines -- scanner and doorlist share one tabbed screen contract */
import * as React from 'react'
import { Check, CheckCircle2, ListChecks, ScanLine, Search, XCircle } from 'lucide-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { EmptyState } from '@/components/ui/empty-state'
import { Input } from '@/components/ui/input'
import { Progress } from '@/components/ui/progress'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { deleteJSON, getJSON, postJSON } from '@/lib/api'
import { displayTime } from '@/lib/dates'
import { EventScannerLinkCard } from '@/components/events/event-scanner-link-card'
import { useViewOnly } from '@/components/templates/app-shell'
import { OwnedChip } from '@/components/OwnedChip'
type Scan = {
  id: number
  name: string
  ticket_type: string
  checked_in_at: string
  device: string | null
  status: 'checked-in' | 'balance-due'
  balance: string | null
  owner?: import('@/components/OwnedChip').Owner | string | null
}
type CheckInBook = {
  event: { id: number; name: string; date: string | null; location: string | null }
  total_count: number
  checked_in_count: number
  recent_scans: Scan[]
  attendees: DoorlistAttendee[]
}
type DoorlistAttendee = {
  id: number
  seat: number
  name: string | null
  email: string | null
  buyer: string
  order: number
  ticket_type: string
  check_in_token: string
  checked_in_at: string | null
  checked_in_by: string | null
  checked_in_device: string | null
  payment_state: 'paid' | 'balance-due'
  balance: string | null
  owner?: import('@/components/OwnedChip').Owner | string | null
}
type ScanResult = {
  scan: {
    verdict: 'admit' | 'admit-with-balance' | 'invalid' | 'wrong-event' | 'cancelled' | 'duplicate'
    attendee?: { id: number; name: string | null; ticket_type: string }
    party?: PartyAttendee[]
    payment?: { open_balance_cents: number }
    message?: string
  }
}
type PartyAttendee = {
  id: number
  name: string | null
  checked_in_at: string | null
  checked_in_by: string | null
  checked_in_device: string | null
}

export function hasRemainingPartyMember(party: PartyAttendee[]) {
  return party.some((attendee) => !attendee.checked_in_at)
}

export function EventCheckInPage({
  eventId,
  onDoorlist,
  onAddWalkIn,
}: {
  eventId: number
  onDoorlist?: () => void
  onAddWalkIn?: () => void
}) {
  const viewOnly = useViewOnly()
  const [cameraDenied, setCameraDenied] = React.useState(false)
  const [verdict, setVerdict] = React.useState<ScanResult['scan']>()
  const [groupParty, setGroupParty] = React.useState<PartyAttendee[]>([])
  const hasRemainingParty = hasRemainingPartyMember(groupParty)
  const bookQuery = useQuery({
    queryKey: ['event-check-in', eventId],
    queryFn: () => getJSON<CheckInBook>(`/api/v1/events/${eventId}/check_in`),
    retry: false,
  })
  const scan = useEventScannerMutation(eventId, setVerdict, setGroupParty)
  React.useEffect(() => {
    if (!verdict || hasRemainingParty) return
    const timeout = window.setTimeout(() => setVerdict(undefined), 2_000)
    return () => window.clearTimeout(timeout)
  }, [hasRemainingParty, verdict])

  const submitToken = React.useCallback(
    (value: string) => {
      const token = value.split('/').filter(Boolean).at(-1)
      if (token) scan.mutate(token)
    },
    [scan],
  )

  const videoRef = React.useRef<HTMLVideoElement>(null)
  useCameraScanner(videoRef, scan.isPending, submitToken, setCameraDenied)

  const book = bookQuery.data
  if (bookQuery.isLoading)
    return <div className="p-6 text-body-sm text-muted-foreground">Loading scanner…</div>
  if (!book)
    return <div className="p-6 text-body-sm text-muted-foreground">Scanner unavailable.</div>
  const percent = book.total_count ? (book.checked_in_count / book.total_count) * 100 : 0

  return (
    <div className="min-h-full bg-muted/40 px-4 py-4 sm:px-6">
      {!viewOnly && <EventScannerLinkCard eventId={eventId} />}
      <div className="mx-auto mt-4 flex w-full max-w-[390px] flex-col">
        <div className="mx-auto flex min-h-[min(780px,calc(100vh-2rem))] w-full max-w-[390px] flex-col overflow-hidden rounded-lg border border-border bg-background shadow-sm">
          <header className="shrink-0 border-b border-border px-4 py-3">
            <p className="text-body-sm font-medium text-foreground">{book.event.name}</p>
            <p className="mt-0.5 text-caption text-muted-foreground">
              {book.event.date ?? 'Event'}
              {book.event.location ? ` · ${book.event.location}` : ''}
            </p>
            <p className="mt-2 text-caption text-foreground">
              Checked in <span className="font-medium tabular-nums">{book.checked_in_count}</span>{' '}
              <span className="text-muted-foreground">of {book.total_count}</span>
            </p>
            <Progress value={percent} className="mt-1.5 h-1" />
          </header>
          <Tabs defaultValue="scan" className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <TabsContent
              value="scan"
              className="m-0 flex min-h-0 flex-1 flex-col overflow-hidden pt-0 data-[state=inactive]:hidden"
            >
              {viewOnly ? (
                <EmptyState
                  variant="quiet"
                  title="Check-in is available to event managers"
                  description="You can still review the event's recent check-ins below."
                />
              ) : cameraDenied ? (
                <CameraDenied onDoorlist={onDoorlist} />
              ) : verdict ? (
                <>
                  <Verdict result={verdict} />
                  <GroupOffer
                    eventId={eventId}
                    party={groupParty}
                    onDone={() => setGroupParty([])}
                  />
                </>
              ) : (
                <CameraStage ref={videoRef} />
              )}
              <RecentScans scans={book.recent_scans} />
            </TabsContent>
            <TabsContent value="doorlist" className="m-0 flex-1 pt-4 data-[state=inactive]:hidden">
              <Doorlist
                eventId={eventId}
                attendees={book.attendees}
                onAddWalkIn={viewOnly ? undefined : onAddWalkIn}
                viewOnly={viewOnly}
              />
            </TabsContent>
            <TabsList className="h-14 w-full shrink-0 rounded-none border-b-0 border-t border-border bg-background p-0">
              <TabsTrigger
                value="scan"
                className="h-full flex-1 flex-col gap-1 rounded-none text-caption"
              >
                <ScanLine className="h-4 w-4" />
                Scan
              </TabsTrigger>
              <TabsTrigger
                value="doorlist"
                className="h-full flex-1 flex-col gap-1 rounded-none text-caption"
              >
                <ListChecks className="h-4 w-4" />
                Doorlist
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </div>
    </div>
  )
}
function Doorlist({
  eventId,
  attendees,
  onAddWalkIn,
  viewOnly,
}: {
  eventId: number
  attendees: DoorlistAttendee[]
  onAddWalkIn?: () => void
  viewOnly: boolean
}) {
  const [search, setSearch] = React.useState('')
  const [status, setStatus] = React.useState<'all' | 'remaining' | 'checked-in'>('all')
  const [ticket, setTicket] = React.useState('all')
  const [payment, setPayment] = React.useState<'all' | 'paid' | 'balance-due'>('all')
  const query = search.trim().toLowerCase()
  const ticketTypes = [...new Set(attendees.map((attendee) => attendee.ticket_type))].sort()
  const filtered = attendees.filter((attendee) => {
    const label = attendee.name ?? `Seat ${attendee.seat} of ${attendee.buyer}'s order`
    const matchesSearch = [
      label,
      attendee.buyer,
      attendee.email ?? '',
      String(attendee.order),
    ].some((value) => value.toLowerCase().includes(query))
    return (
      matchesSearch &&
      (status === 'all' ||
        (status === 'checked-in' ? attendee.checked_in_at : !attendee.checked_in_at)) &&
      (ticket === 'all' || attendee.ticket_type === ticket) &&
      (payment === 'all' || attendee.payment_state === payment)
    )
  })
  const count = (nextStatus: 'all' | 'remaining' | 'checked-in') =>
    attendees.filter((attendee) => {
      const label = attendee.name ?? `Seat ${attendee.seat} of ${attendee.buyer}'s order`
      const matchesSearch = [
        label,
        attendee.buyer,
        attendee.email ?? '',
        String(attendee.order),
      ].some((value) => value.toLowerCase().includes(query))
      return (
        matchesSearch &&
        (nextStatus === 'all' ||
          (nextStatus === 'checked-in' ? attendee.checked_in_at : !attendee.checked_in_at)) &&
        (ticket === 'all' || attendee.ticket_type === ticket) &&
        (payment === 'all' || attendee.payment_state === payment)
      )
    }).length
  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <div className="shrink-0 space-y-2 border-b border-border px-4 pb-3">
        {onAddWalkIn && (
          <Button type="button" className="h-8 w-full text-caption" onClick={onAddWalkIn}>
            Add walk-in
          </Button>
        )}
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="h-8 pl-8 text-caption"
            placeholder="Search name, email, or order"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {(['all', 'remaining', 'checked-in'] as const).map((value) => (
            <Button
              key={value}
              type="button"
              variant="outline"
              size="sm"
              className={value === status ? 'h-7 bg-primary/5 text-caption' : 'h-7 text-caption'}
              onClick={() => setStatus(value)}
            >
              {value === 'all' ? 'All' : value === 'remaining' ? 'Remaining' : 'Checked in'}{' '}
              <span className="ml-1 tabular-nums text-muted-foreground">{count(value)}</span>
            </Button>
          ))}
          <FilterButtons
            label="Ticket"
            values={['all', ...ticketTypes]}
            value={ticket}
            onChange={setTicket}
          />
          <FilterButtons
            label="Payment"
            values={['all', 'paid', 'balance-due']}
            value={payment}
            onChange={(value) => setPayment(value as typeof payment)}
          />
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        {filtered.length === 0 ? (
          <EmptyState
            variant="quiet"
            title={`No one matches “${search}”.`}
            description="Search by attendee name, buyer name, email, or order."
          />
        ) : (
          filtered.map((attendee) => (
            <DoorlistRow
              key={attendee.id}
              eventId={eventId}
              attendee={attendee}
              viewOnly={viewOnly}
            />
          ))
        )}
      </div>
    </div>
  )
}
function FilterButtons({
  label,
  values,
  value,
  onChange,
}: {
  label: string
  values: string[]
  value: string
  onChange: (value: string) => void
}) {
  return (
    <>
      {values.map((option) => (
        <Button
          key={option}
          type="button"
          variant="outline"
          size="sm"
          className={value === option ? 'h-7 bg-primary/5 text-caption' : 'h-7 text-caption'}
          onClick={() => onChange(option)}
        >
          {label} · {option === 'all' ? 'All' : option}
        </Button>
      ))}
    </>
  )
}
function DoorlistRow({
  eventId,
  attendee,
  viewOnly,
}: {
  eventId: number
  attendee: DoorlistAttendee
  viewOnly: boolean
}) {
  const queryClient = useQueryClient()
  const [pending, setPending] = React.useState(false)
  const label = attendee.name ?? `Seat ${attendee.seat} of ${attendee.buyer}'s order`
  async function toggle() {
    setPending(true)
    try {
      if (attendee.checked_in_at)
        await deleteJSON(`/api/v1/events/${eventId}/scan/${attendee.check_in_token}/check_in`, {
          device_label: /Mobile|Android|iPhone|iPad/i.test(navigator.userAgent)
            ? 'Staff phone'
            : 'Staff browser',
        })
      else
        await postJSON(`/api/v1/events/${eventId}/scan/${attendee.check_in_token}/check_in`, {
          device_label: /Mobile|Android|iPhone|iPad/i.test(navigator.userAgent)
            ? 'Staff phone'
            : 'Staff browser',
        })
      await queryClient.invalidateQueries({ queryKey: ['event-check-in', eventId] })
    } finally {
      setPending(false)
    }
  }
  return (
    <div className="flex items-center gap-3 border-b border-border px-4 py-2.5">
      <div className="min-w-0 flex-1">
        <p className="text-body-sm font-medium text-foreground">
          {label} <OwnedChip owner={attendee.owner} />
        </p>
        <p className="text-caption text-muted-foreground">
          {attendee.ticket_type} · Order #{attendee.order}
          {attendee.balance && (
            <span className="ml-1.5 text-warning">Balance due {attendee.balance}</span>
          )}
        </p>
        {attendee.checked_in_at && (
          <p className="text-caption text-success">
            Checked in {formatScanTime(attendee.checked_in_at)} ·{' '}
            {attendee.checked_in_device ?? attendee.checked_in_by}
          </p>
        )}
      </div>
      {!viewOnly &&
        !(attendee.owner && typeof attendee.owner !== 'string' && attendee.owner.connection) && (
          <button
            type="button"
            aria-label={`${attendee.checked_in_at ? 'Undo check-in' : 'Check in'} ${label}`}
            disabled={pending}
            onClick={() => void toggle()}
            className={`flex size-8 shrink-0 items-center justify-center rounded-full border ${attendee.checked_in_at ? 'border-success bg-success/15 text-success' : 'border-input text-muted-foreground'}`}
          >
            <Check className="size-4" strokeWidth={1.5} />
          </button>
        )}
    </div>
  )
}

export function GroupOffer({
  eventId,
  party,
  onDone,
}: {
  eventId: number
  party: PartyAttendee[]
  onDone: () => void
}) {
  const remaining = party.filter((attendee) => !attendee.checked_in_at)
  const [expanded, setExpanded] = React.useState(false)
  const [selected, setSelected] = React.useState<number[]>(remaining.map((attendee) => attendee.id))
  const queryClient = useQueryClient()
  React.useEffect(
    () =>
      setSelected(
        party.filter((attendee) => !attendee.checked_in_at).map((attendee) => attendee.id),
      ),
    [party],
  )
  if (remaining.length === 0) return null

  async function checkInSelected() {
    await postJSON(`/api/v1/events/${eventId}/group_check_in`, {
      attendee_ids: selected,
      device_label: /Mobile|Android|iPhone|iPad/i.test(navigator.userAgent)
        ? 'Staff phone'
        : 'Staff browser',
    })
    await queryClient.invalidateQueries({ queryKey: ['event-check-in', eventId] })
    onDone()
  }

  return (
    <div className="shrink-0 border-b border-border px-4 py-3">
      {!expanded ? (
        <>
          <Button type="button" className="h-8 w-full px-3" onClick={() => void checkInSelected()}>
            Check in the rest of the party ({remaining.length} more)
          </Button>
          <Button
            type="button"
            variant="outline"
            className="mt-2 h-8 w-full px-3 text-caption"
            onClick={() => setExpanded(true)}
          >
            Pick individuals instead
          </Button>
        </>
      ) : (
        <>
          <p className="mb-2 text-caption font-medium text-foreground">Who else is arriving?</p>
          <div className="space-y-2">
            {party.map((attendee) => {
              const checkedIn = Boolean(attendee.checked_in_at)
              return (
                <label key={attendee.id} className="flex items-start gap-2 text-caption">
                  <Checkbox
                    checked={checkedIn || selected.includes(attendee.id)}
                    disabled={checkedIn}
                    onCheckedChange={(checked: boolean | 'indeterminate') =>
                      setSelected((ids) =>
                        checked === true
                          ? [...new Set([...ids, attendee.id])]
                          : ids.filter((id) => id !== attendee.id),
                      )
                    }
                    aria-label={`Check in ${attendee.name ?? 'Unnamed attendee'}`}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block text-body-sm text-foreground">
                      {attendee.name ?? 'Unnamed attendee'}
                    </span>
                    {checkedIn && (
                      <span className="text-success">
                        Checked in {formatScanTime(attendee.checked_in_at ?? undefined)} ·{' '}
                        {attendee.checked_in_device ?? attendee.checked_in_by}
                      </span>
                    )}
                  </span>
                </label>
              )
            })}
          </div>
          <Button
            type="button"
            className="mt-3 h-8 w-full px-3"
            disabled={selected.length === 0}
            onClick={() => void checkInSelected()}
          >
            Check in {selected.length} selected
          </Button>
          <Button
            type="button"
            variant="outline"
            className="mt-2 h-8 w-full px-3 text-caption"
            onClick={onDone}
          >
            Decline
          </Button>
        </>
      )}
    </div>
  )
}
function useCameraScanner(
  videoRef: React.RefObject<HTMLVideoElement | null>,
  scanPending: boolean,
  submitToken: (value: string) => void,
  setCameraDenied: React.Dispatch<React.SetStateAction<boolean>>,
) {
  React.useEffect(() => {
    let stream: MediaStream | undefined
    let stopped = false
    async function startCamera() {
      if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
        setCameraDenied(true)
        return
      }
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } })
        if (stopped) return
        if (videoRef.current) videoRef.current.srcObject = stream
        const Detector = (
          globalThis as typeof globalThis & {
            BarcodeDetector?: new () => {
              detect: (source: HTMLVideoElement) => Promise<Array<{ rawValue: string }>>
            }
          }
        ).BarcodeDetector
        if (!Detector) return
        const detector = new Detector()
        const loop = async () => {
          if (stopped || !videoRef.current || scanPending) return
          const codes = await detector.detect(videoRef.current)
          if (codes[0]?.rawValue) submitToken(codes[0].rawValue)
          if (!stopped) window.setTimeout(loop, 250)
        }
        if (videoRef.current) void videoRef.current.play().then(loop)
      } catch {
        setCameraDenied(true)
      }
    }
    void startCamera()
    return () => {
      stopped = true
      stream?.getTracks().forEach((track) => track.stop())
    }
  }, [scanPending, submitToken, setCameraDenied, videoRef])
}
function useEventScannerMutation(
  eventId: number,
  setVerdict: React.Dispatch<React.SetStateAction<ScanResult['scan'] | undefined>>,
  setGroupParty?: React.Dispatch<React.SetStateAction<PartyAttendee[]>>,
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (token: string) => {
      const result = await getJSON<ScanResult>(`/api/v1/events/${eventId}/scan/${token}`)
      if (!['admit', 'admit-with-balance'].includes(result.scan.verdict)) return result.scan

      const checkIn = await postJSON<{
        check_in: { verdict: string; occurred_at?: string; staffer?: string; device?: string }
      }>(`/api/v1/events/${eventId}/scan/${token}/check_in`, {
        device_label: /Mobile|Android|iPhone|iPad/i.test(navigator.userAgent)
          ? 'Staff phone'
          : 'Staff browser',
      })
      const party = result.scan.party?.map((attendee) =>
        attendee.id === result.scan.attendee?.id
          ? {
              ...attendee,
              checked_in_at: checkIn.check_in.occurred_at ?? new Date().toISOString(),
              checked_in_by: checkIn.check_in.staffer ?? null,
              checked_in_device: checkIn.check_in.device ?? null,
            }
          : attendee,
      )
      if (checkIn.check_in.verdict !== 'duplicate') return { ...result.scan, party }

      return {
        ...result.scan,
        party,
        verdict: 'duplicate' as const,
        message: `First scanned ${formatScanTime(checkIn.check_in.occurred_at)} by ${checkIn.check_in.device ?? checkIn.check_in.staffer ?? 'another device'}`,
      }
    },
    onSuccess: (result) => {
      setVerdict(result)
      setGroupParty?.(result.party ?? [])
      queryClient.invalidateQueries({ queryKey: ['event-check-in', eventId] })
    },
  })
}
const CameraStage = React.forwardRef<HTMLVideoElement>(function CameraStage(_, ref) {
  return (
    <div className="relative aspect-square w-full shrink-0 bg-foreground">
      <video
        ref={ref}
        className="h-full w-full object-cover"
        muted
        playsInline
        aria-label="Ticket QR scanner"
      />
      <span className="absolute left-8 top-8 h-8 w-8 rounded-tl border-l-2 border-t-2 border-background/70" />
      <span className="absolute right-8 top-8 h-8 w-8 rounded-tr border-r-2 border-t-2 border-background/70" />
      <span className="absolute bottom-4 inset-x-0 text-center text-caption text-background/70">
        Point the camera at the ticket QR code
      </span>
    </div>
  )
})
function CameraDenied({ onDoorlist }: { onDoorlist?: () => void }) {
  return (
    <div className="flex flex-1 items-center justify-center px-4">
      <EmptyState
        title="Camera access is off"
        description="Allow camera access in your browser settings, then reload. You can still check people in from the doorlist."
        action={
          <Button type="button" onClick={onDoorlist}>
            Open the doorlist
          </Button>
        }
      />
    </div>
  )
}
function Verdict({ result }: { result: ScanResult['scan'] }) {
  const admitted = result.verdict === 'admit' || result.verdict === 'admit-with-balance'
  const balance = formatBalance(result)
  return (
    <div
      className={`flex aspect-square w-full shrink-0 flex-col items-center justify-center gap-2 px-6 text-center ${verdictTone(result)}`}
    >
      {verdictIcon(result)}
      <p className="text-body font-semibold">{verdictTitle(result)}</p>
      {result.attendee && (
        <>
          <p className="text-body-sm">{result.attendee.name ?? 'Unnamed attendee'}</p>
          <p className="text-caption text-muted-foreground">{result.attendee.ticket_type}</p>
        </>
      )}
      {balance && <Badge variant="warning">Balance due {balance}</Badge>}
      <p className="text-caption text-muted-foreground">
        {result.message ?? verdictMessage(admitted, balance)}
      </p>
    </div>
  )
}
function formatBalance(result: ScanResult['scan']) {
  const cents = result.payment?.open_balance_cents ?? 0
  return cents > 0 ? `$${(cents / 100).toFixed(2)}` : null
}
function verdictTone(result: ScanResult['scan']) {
  if (result.verdict === 'admit-with-balance') return 'bg-warning/15'
  if (result.verdict === 'admit') return 'bg-success/15'
  return 'bg-destructive/10'
}
function verdictIcon(result: ScanResult['scan']) {
  if (result.verdict === 'admit-with-balance')
    return <CheckCircle2 className="h-10 w-10 text-warning" />
  if (result.verdict === 'admit') return <CheckCircle2 className="h-10 w-10 text-success" />
  return <XCircle className="h-10 w-10 text-destructive" />
}
function verdictTitle(result: ScanResult['scan']) {
  if (result.verdict === 'admit' || result.verdict === 'admit-with-balance') return 'Checked in'
  if (result.verdict === 'wrong-event') return 'Wrong Event'
  if (result.verdict === 'cancelled') return 'Cancelled'
  if (result.verdict === 'duplicate') return 'Already checked in'
  return 'Invalid code'
}
function formatScanTime(value?: string) {
  if (!value) return 'earlier'
  return displayTime(value)
}
function verdictMessage(admitted: boolean, balance: string | null) {
  if (balance) return 'Check in anyway'
  if (admitted) return 'Ready for the next scan'
  return 'This code cannot check anyone in.'
}
function RecentScans({ scans }: { scans: Scan[] }) {
  return (
    <div className="min-h-0 flex-1 overflow-y-auto">
      <p className="border-b border-border px-4 py-2 text-caption text-muted-foreground">
        Recent scans
      </p>
      {scans.map((scan) => (
        <div key={scan.id} className="flex items-center gap-3 border-b border-border px-4 py-2">
          <span
            className={`h-1.5 w-1.5 shrink-0 rounded-full ${scan.status === 'balance-due' ? 'bg-warning' : 'bg-success'}`}
          />
          <div className="min-w-0 flex-1">
            <p className="text-caption font-medium">{scan.name}</p>
            <p className="text-caption text-muted-foreground">
              {scan.ticket_type}
              {scan.balance ? ` · balance due ${scan.balance}` : ''}
            </p>
          </div>
          <p className="shrink-0 text-caption tabular-nums text-muted-foreground">
            {displayTime(scan.checked_in_at)}
          </p>
        </div>
      ))}
    </div>
  )
}
